import type {
  AgendaCategory,
  Allocation,
  CalendarEvent,
  BudgetMonth,
  Deployment,
  ErrorGroup,
  Expense,
  Frontier,
  GoldEntry,
  Host,
  MonitorAlert,
  Occurrence,
  PersonCost,
  PullRequest,
  Repository,
  Routine,
  ServiceStatus,
  Ticket,
} from '@sistemas/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'
import type { AuditEntry } from '@/modules/projects/store'
import { mapTickets } from '@/modules/tickets/tickets-api'

/**
 * Leitura das capacidades alimentadas por integração e das financeiras
 * (C4 a C10).
 *
 * Este módulo existe porque o frontend nascia com um `seed.ts` de "dados de
 * referência": hosts com CPU em 34%, serviços com uptime fixo, 21 repositórios
 * todos com branch `production`. Eram números plausíveis e **falsos**, e um
 * painel de monitoramento que mostra número falso é pior que um painel vazio:
 * o vazio manda investigar, o falso manda relaxar.
 *
 * Agora tudo vem do schema `sistemas`. Quem preenche host, serviço, fronteira,
 * repositório, PR, deploy, erro e alerta é o **worker** (`apps/worker`); a
 * tela só lê. Enquanto o worker não roda, a tela mostra vazio — que é a
 * verdade sobre o estado das integrações.
 */

export type CoreReadResult<T> = { ok: true; data: T } | { ok: false; reason: string }

function dbError(error: { message?: string } | null, fallback: string): string {
  return describeDbError(error, fallback)
}

export interface PullRequestRow {
  id: string; repository_id: string; number: number; title: string; author_login: string
  author_member_id: string | null; author_avatar_url?: string | null; state: PullRequest['state']; is_draft: boolean
  checks_status: PullRequest['checksStatus']; checks?: PullRequest['checks'] | null; approvals: number; changes_requested: number
  opened_at: string; merged_at: string | null; closed_at: string | null
  activity_key: string | null; additions: number; deletions: number
  base_ref: string | null; head_ref: string | null; merged_by_login: string | null; url: string | null
}

export function mapPullRequest(r: PullRequestRow): PullRequest {
  return {
    id: r.id,
    repositoryId: r.repository_id,
    number: r.number,
    title: r.title,
    authorLogin: r.author_login,
    state: r.state,
    isDraft: r.is_draft,
    checksStatus: r.checks_status,
    ...(Array.isArray(r.checks) && r.checks.length > 0 ? { checks: r.checks } : {}),
    approvals: num(r.approvals, 0),
    changesRequested: num(r.changes_requested, 0),
    openedAt: r.opened_at,
    additions: num(r.additions, 0),
    deletions: num(r.deletions, 0),
    ...(r.author_member_id ? { authorMemberId: r.author_member_id } : {}),
    ...(r.author_avatar_url ? { authorAvatarUrl: r.author_avatar_url } : {}),
    ...(r.merged_at ? { mergedAt: r.merged_at } : {}),
    ...(r.closed_at ? { closedAt: r.closed_at } : {}),
    ...(r.activity_key ? { activityKey: r.activity_key } : {}),
    ...(r.base_ref ? { baseRef: r.base_ref } : {}),
    ...(r.head_ref ? { headRef: r.head_ref } : {}),
    ...(r.merged_by_login ? { mergedByLogin: r.merged_by_login } : {}),
    ...(r.url ? { url: r.url } : {}),
  }
}

export interface DeploymentRow {
  id: string; repository_id: string; environment: Deployment['environment']
  version: string; status: Deployment['status']; finished_at: string
  duration_seconds: number; workflow_name: string | null; actor_login: string | null
  head_sha: string | null; run_url: string | null; run_attempt: number | null; event: string | null
}

export function mapDeployment(r: DeploymentRow): Deployment {
  return {
    id: r.id,
    repositoryId: r.repository_id,
    environment: r.environment,
    version: r.version,
    status: r.status,
    finishedAt: r.finished_at,
    durationSeconds: num(r.duration_seconds, 0),
    ...(r.workflow_name ? { workflowName: r.workflow_name } : {}),
    ...(r.actor_login ? { actorLogin: r.actor_login } : {}),
    ...(r.head_sha ? { headSha: r.head_sha } : {}),
    ...(r.run_url ? { runUrl: r.run_url } : {}),
    ...(r.run_attempt !== null && r.run_attempt !== undefined ? { runAttempt: r.run_attempt } : {}),
    ...(r.event ? { event: r.event } : {}),
  }
}

function num(value: unknown, fallback: number): number {
  if (value === null || value === undefined) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/** `last_seen_at` absoluto vira "há quantos segundos", que é como a tela lê. */
function secondsSince(iso: string | null): number {
  if (!iso) return Number.MAX_SAFE_INTEGER
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  return Number.isFinite(diff) && diff >= 0 ? Math.round(diff) : 0
}

export interface CoreCatalog {
  hosts: Host[]
  categories: AgendaCategory[]
  events: CalendarEvent[]
  services: ServiceStatus[]
  frontiers: Frontier[]
  repositories: Repository[]
  routines: Routine[]
  occurrences: Occurrence[]
  tickets: Ticket[]
  pullRequests: PullRequest[]
  deployments: Deployment[]
  errorGroups: ErrorGroup[]
  alerts: MonitorAlert[]
  gold: GoldEntry[]
  costs: PersonCost[]
  allocations: Allocation[]
  budgets: BudgetMonth[]
  expenses: Expense[]
  audit: AuditEntry[]
}

function groupIds<T extends Record<string, unknown>>(
  rows: readonly T[],
  keyField: keyof T,
  valueField: keyof T,
): Map<string, string[]> {
  const map = new Map<string, string[]>()
  for (const row of rows) {
    const key = String(row[keyField])
    const value = String(row[valueField])
    const current = map.get(key)
    if (current) current.push(value)
    else map.set(key, [value])
  }
  return map
}

/**
 * Carga única de tudo que não é C1/C2/C3.
 *
 * Falha parcial derruba a leitura inteira: um dashboard com metade das fontes
 * carregadas e metade vazias é indistinguível de um departamento parado.
 */
export async function fetchCoreCatalog(): Promise<CoreReadResult<CoreCatalog>> {
  const sb = getSupabase()

  const [
    hosts,
    services,
    frontiers,
    repositories,
    projectRepos,
    categories,
    routines,
    routineParticipants,
    occurrences,
    events,
    eventParticipants,
    tickets,
    ticketTriages,
    ticketActivities,
    pullRequests,
    deployments,
    errorGroups,
    alerts,
    alertAcks,
    gold,
    costs,
    allocations,
    budgets,
    expenses,
    audit,
  ] = await Promise.all([
    sb.from('host').select('*').order('name'),
    sb.from('service').select('*').order('name'),
    sb.from('frontier').select('*').order('name'),
    sb.from('repository').select('*').order('name'),
    sb.from('project_repository').select('project_id, repository_id'),
    sb.from('agenda_category').select('*').order('position').order('name'),
    // Inclui inativas: a tela de Rotinas precisa listar e reativar o que foi
    // desligado, e a ocorrência antiga continua apontando para a rotina.
    sb.from('routine').select('*').order('name'),
    sb.from('routine_participant').select('routine_id, member_id'),
    sb.from('occurrence').select('*').order('date', { ascending: false }).limit(500),
    // `calendar_event_visible`, não a tabela: é a view que aplica R7/R8 —
    // compromisso privado de terceiro não vem, e `ocupado` vem mascarado.
    sb.from('calendar_event_visible').select('*').order('date').limit(1000),
    sb.from('calendar_event_participant').select('event_id, member_id'),
    sb.from('ticket').select('*').order('opened_at', { ascending: false }).limit(500),
    sb.from('ticket_triage').select('*'),
    sb.from('ticket_activity').select('ticket_id, activity_id'),
    sb.from('pull_request').select('*').order('opened_at', { ascending: false }).limit(300),
    sb.from('deployment').select('*').order('finished_at', { ascending: false }).limit(300),
    sb.from('error_group').select('*').order('last_seen', { ascending: false }).limit(200),
    // Só os abertos: o worker marca `resolved_at` quando o alerta some do
    // Alertmanager. Sem este filtro, alerta já resolvido seguia no Dashboard.
    sb.from('alert').select('*').is('resolved_at', null).order('since', { ascending: false }).limit(200),
    sb.from('alert_ack').select('alert_id'),
    sb.from('gold_entry').select('*').order('created_at', { ascending: false }).limit(500),
    sb.from('person_cost').select('*').order('effective_from', { ascending: false }),
    sb.from('allocation').select('*'),
    sb.from('budget_month').select('*').order('month', { ascending: false }),
    sb.from('expense').select('*').order('incurred_on', { ascending: false }).limit(500),
    sb.from('audit_entry').select('*').order('occurred_at', { ascending: false }).limit(200),
  ])

  const failure = [
    hosts, services, frontiers, repositories, projectRepos, categories, routines,
    routineParticipants, occurrences, events, eventParticipants,
    tickets, ticketTriages, ticketActivities,
    pullRequests, deployments, errorGroups, alerts, alertAcks,
    gold, costs, allocations, budgets, expenses, audit,
  ].find((r) => r.error)?.error

  if (failure) {
    return { ok: false, reason: dbError(failure, 'Falha ao carregar os dados do departamento.') }
  }

  const rows = <T,>(res: { data: unknown }): T[] => (res.data ?? []) as T[]

  const reposByProject = groupIds(
    rows<{ project_id: string; repository_id: string }>(projectRepos),
    'repository_id',
    'project_id',
  )
  const participantsByEvent = groupIds(
    rows<{ event_id: string; member_id: string }>(eventParticipants),
    'event_id',
    'member_id',
  )
  const participantsByRoutine = groupIds(
    rows<{ routine_id: string; member_id: string }>(routineParticipants),
    'routine_id',
    'member_id',
  )
  const ackedAlerts = new Set(rows<{ alert_id: string }>(alertAcks).map((a) => a.alert_id))

  return {
    ok: true,
    data: {
      hosts: rows<Record<string, never>>(hosts).map((r) => {
        const row = r as unknown as {
          id: string; name: string; address: string; role: string
          cpu_percent: number | null; memory_percent: number | null
          disk_percent: number | null; uptime_days: number | null; last_seen_at: string | null
        }
        return {
          id: row.id,
          name: row.name,
          address: row.address,
          role: row.role,
          // Métrica ausente fica null: um host que sumiu do Prometheus não
          // pode aparecer com "0% de CPU", que parece um servidor ocioso.
          cpuPercent: row.cpu_percent === null ? null : num(row.cpu_percent, 0),
          memoryPercent: row.memory_percent === null ? null : num(row.memory_percent, 0),
          diskPercent: row.disk_percent === null ? null : num(row.disk_percent, 0),
          uptimeDays: row.uptime_days === null ? null : num(row.uptime_days, 0),
          lastSeenSecondsAgo: secondsSince(row.last_seen_at),
        } satisfies Host
      }),

      services: rows<{
        id: string; name: string; host_id: string | null
        state: ServiceStatus['state']; uptime_days: number | null
      }>(services).map((r) => ({
        id: r.id,
        name: r.name,
        hostId: r.host_id ?? '',
        state: r.state,
        uptimeDays: num(r.uptime_days, 0),
      })),

      // `key` é o identificador estável da fronteira (é por ela que o worker
      // faz upsert); o uuid da linha não significa nada para a tela.
      frontiers: rows<{
        key: string; name: string; state: Frontier['state']; last_event_label: string | null
      }>(frontiers).map((r) => ({
        id: r.key,
        name: r.name,
        state: r.state,
        lastEventLabel: r.last_event_label ?? '',
      })),

      repositories: rows<{ id: string; name: string; has_production: boolean }>(repositories).map(
        (r) => ({
          id: r.id,
          name: r.name,
          hasProduction: r.has_production,
          projectIds: reposByProject.get(r.id) ?? [],
        }),
      ),

      categories: rows<{
        id: string; name: string; color: AgendaCategory['color']
        description: string | null; position: number; is_active: boolean
      }>(categories).map((r) => ({
        id: r.id,
        name: r.name,
        color: r.color,
        position: r.position,
        isActive: r.is_active,
        ...(r.description ? { description: r.description } : {}),
      })),

      routines: rows<{
        id: string; name: string; kind: Routine['kind']; category_id: string | null
        weekday: number | null; month_day: number | null; week_of_month: number | null
        time_of_day: string; duration_minutes: number; owner_id: string
        non_business_day_policy: Routine['nonBusinessDayPolicy']; is_active: boolean
      }>(routines).map((r) => ({
        id: r.id,
        name: r.name,
        kind: r.kind,
        // `time` do Postgres vem como `09:00:00`; a UI usa `09:00`.
        time: r.time_of_day.slice(0, 5),
        durationMinutes: r.duration_minutes,
        ownerId: r.owner_id,
        nonBusinessDayPolicy: r.non_business_day_policy,
        isActive: r.is_active,
        participantIds: participantsByRoutine.get(r.id) ?? [],
        ...(r.category_id ? { categoryId: r.category_id } : {}),
        ...(r.weekday !== null ? { weekday: r.weekday } : {}),
        ...(r.week_of_month !== null ? { weekOfMonth: r.week_of_month } : {}),
        ...(r.month_day !== null ? { monthDay: r.month_day } : {}),
      })),

      events: rows<{
        id: string; title: string; description: string | null; location: string | null
        category_id: string | null; date: string; time_of_day: string | null
        duration_minutes: number; visibility: CalendarEvent['visibility']
        owner_id: string; can_see_detail: boolean
      }>(events).map((r) => ({
        id: r.id,
        title: r.title,
        date: r.date,
        durationMinutes: r.duration_minutes,
        visibility: r.visibility,
        canSeeDetail: r.can_see_detail,
        ownerId: r.owner_id,
        participantIds: participantsByEvent.get(r.id) ?? [],
        ...(r.time_of_day ? { time: r.time_of_day.slice(0, 5) } : {}),
        ...(r.description ? { description: r.description } : {}),
        ...(r.location ? { location: r.location } : {}),
        ...(r.category_id ? { categoryId: r.category_id } : {}),
      })),

      occurrences: rows<{
        id: string; routine_id: string; date: string; original_date: string
        status: Occurrence['status']
        agenda: string | null; minutes: string | null; attendance: Occurrence['attendance'] | null
      }>(occurrences).map((r) => ({
        id: r.id,
        routineId: r.routine_id,
        date: r.date,
        originalDate: r.original_date,
        status: r.status,
        attendance: r.attendance ?? {},
        ...(r.agenda ? { agenda: r.agenda } : {}),
        ...(r.minutes ? { minutes: r.minutes } : {}),
      })),

      tickets: mapTickets(rows(tickets), rows(ticketTriages), rows(ticketActivities)),

      pullRequests: rows<PullRequestRow>(pullRequests).map((r) => mapPullRequest(r)),

      deployments: rows<DeploymentRow>(deployments).map((r) => mapDeployment(r)),

      errorGroups: rows<{
        id: string; service_id: string | null; title: string; occurrences: number
        affected_users: number; first_seen: string; last_seen: string
        status: ErrorGroup['status']; activity_id: string | null
      }>(errorGroups).map((r) => ({
        id: r.id,
        serviceId: r.service_id ?? '',
        title: r.title,
        occurrences: num(r.occurrences, 0),
        affectedUsers: num(r.affected_users, 0),
        firstSeen: r.first_seen,
        lastSeen: r.last_seen,
        // Tendência é comparação entre janelas, que o coletor ainda não envia.
        // `estavel` é o neutro honesto — inventar "subindo" criaria urgência falsa.
        trend: 'estavel',
        status: r.status,
        ...(r.activity_id ? { activityId: r.activity_id } : {}),
      })),

      alerts: rows<{
        id: string; severity: MonitorAlert['severity']; source: MonitorAlert['source']
        source_ref: string; title: string; detail: string; since: string
      }>(alerts).map((r) => ({
        id: r.id,
        severity: r.severity,
        source: r.source,
        sourceId: r.source_ref,
        title: r.title,
        detail: r.detail,
        since: r.since,
        acknowledged: ackedAlerts.has(r.id),
      })),

      gold: rows<{
        id: string; member_id: string; points: number; reason: GoldEntry['reason']
        activity_key: string | null; description: string; month: string; created_at: string
      }>(gold).map((r) => ({
        id: r.id,
        memberId: r.member_id,
        points: num(r.points, 0),
        reason: r.reason,
        description: r.description,
        month: r.month,
        createdAt: r.created_at,
        ...(r.activity_key ? { activityKey: r.activity_key } : {}),
      })),

      costs: rows<{
        member_id: string; effective_from: string; salary: number | string | null
        charges: number | string | null; benefits: number | string | null
        monthly_total: number | string | null
      }>(costs).map((r) => ({
        memberId: r.member_id,
        effectiveFrom: r.effective_from,
        monthlyTotal: num(r.monthly_total, 0),
        ...(r.salary !== null ? { salary: num(r.salary, 0) } : {}),
        ...(r.charges !== null ? { charges: num(r.charges, 0) } : {}),
        ...(r.benefits !== null ? { benefits: num(r.benefits, 0) } : {}),
      })),

      allocations: rows<{
        id: string; member_id: string; project_id: string; month: string
        percent: number | string
      }>(allocations).map((r) => ({
        id: r.id,
        memberId: r.member_id,
        projectId: r.project_id,
        month: r.month,
        percent: num(r.percent, 0),
      })),

      // `budget_month` tem `month` como chave primária: não há id separado.
      budgets: rows<{ month: string; amount: number | string; source: string | null }>(
        budgets,
      ).map((r) => ({
        id: r.month,
        month: r.month,
        budgetBrl: num(r.amount, 0),
        // `source` (migration budget_v2): só `padrao` pode ser reescrito pelo
        // budget padrão. Linha anterior à coluna é decisão manual.
        isManual: r.source !== 'padrao',
      })),

      expenses: rows<{
        id: string; description: string | null; vendor: string | null
        amount: number | string; currency: Expense['currency']; amount_brl: number | string
        fx_rate: number | string | null; incurred_on: string; splits: Expense['splits'] | null
        created_by: string; category: string | null; fx_rate_date: string | null
        fx_source: string | null; receipt_name: string | null; recurring: boolean
        renewal_date: string | null; installment_group?: string | null
        installment_number?: number | null; installment_count?: number | null
      }>(expenses).map((r) => ({
        id: r.id,
        paidAt: r.incurred_on,
        vendor: r.vendor ?? '',
        category: r.category ?? '',
        currency: r.currency,
        originalAmount: num(r.amount, 0),
        fxRate: num(r.fx_rate, 1),
        fxRateDate: r.fx_rate_date ?? r.incurred_on,
        fxSource: r.fx_source ?? '',
        amountBrl: num(r.amount_brl, 0),
        receiptName: r.receipt_name ?? '',
        splits: r.splits ?? [],
        recurring: r.recurring,
        createdBy: r.created_by,
        ...(r.description ? { description: r.description } : {}),
        ...(r.renewal_date ? { renewalDate: r.renewal_date } : {}),
        ...(r.installment_group && r.installment_number && r.installment_count
          ? {
              installment: {
                group: r.installment_group,
                number: r.installment_number,
                count: r.installment_count,
              },
            }
          : {}),
      })),

      audit: rows<{
        id: string; actor_id: string | null; action: string; resource: string
        detail: string; occurred_at: string
      }>(audit).map((r) => ({
        id: r.id,
        action: r.action,
        resource: r.resource,
        detail: r.detail,
        authorId: r.actor_id ?? '',
        at: r.occurred_at,
      })),
    },
  }
}
